/** SPU-level product identifier in catalog context. */
export type ProductId = string;
export type VariantId = string;

/** Catalog product type key (`CatalogProductTypeMeta.code`), UPPER_SNAKE — extensible beyond seed defaults. */
export type ProductType = string;

/**
 * Hard cap on product `description` length, shared by API DTOs and admin UI
 * so the counter + server validation never drift. 2000 chars covers richest
 * PDP copy (Amazon-style) while staying well below DB text-row limits and
 * keeping payloads cheap.
 */
export const PRODUCT_DESCRIPTION_MAX_LENGTH = 2000;

export type CanonicalProductTag = {
  /** Namespaced value stored on `Product.tags` (e.g. `routine:nettoyant`). */
  value: string;
  label: string;
};

export type CanonicalProductTagGroup = {
  /** Family code (`routine`, `skin`, …). */
  code: string;
  name: string;
  description: string;
  tags: readonly CanonicalProductTag[];
};

/**
 * Product tag families for Configuration initiale « Installer les modèles ».
 * Must stay aligned with shared regimen / quiz / PLP prefixes.
 */
export const CANONICAL_PRODUCT_TAG_GROUPS: readonly CanonicalProductTagGroup[] = [
  {
    code: 'routine',
    name: 'Étape routine',
    description:
      'Obligatoire pour apparaître dans Regimen Builder (`routine-match`). Un seul tag étape par produit.',
    tags: [
      { value: 'routine:nettoyant', label: 'Nettoyant' },
      { value: 'routine:brume', label: 'Brume / essence' },
      { value: 'routine:serum', label: 'Sérum ciblé' },
      { value: 'routine:contour-yeux', label: 'Contour yeux' },
      { value: 'routine:spf', label: 'SPF' },
      { value: 'routine:double-nettoyage', label: 'Double nettoyage' },
      { value: 'routine:tonique', label: 'Tonique' },
      { value: 'routine:actif-nuit', label: 'Actif nuit' },
      { value: 'routine:soin-traitant', label: 'Soin traitant' },
      { value: 'routine:soin-reparateur', label: 'Soin réparateur' },
      { value: 'routine:creme-barriere', label: 'Crème barrière' },
    ],
  },
  {
    code: 'skin',
    name: 'Type de peau',
    description: 'Réponses quiz regimen / analyse peau (`skin:*`).',
    tags: [
      { value: 'skin:seche', label: 'Peau sèche' },
      { value: 'skin:grasse', label: 'Peau grasse' },
      { value: 'skin:mixte', label: 'Peau mixte' },
      { value: 'skin:sensible', label: 'Peau sensible' },
      { value: 'skin:normale', label: 'Peau normale' },
    ],
  },
  {
    code: 'concern',
    name: 'Préoccupation',
    description:
      'Filtre PLP boutique `?concern=` et matching diagnostic (`concern:*`).',
    tags: [
      { value: 'concern:hydratation', label: 'Hydratation' },
      { value: 'concern:antiage', label: 'Anti-âge' },
      { value: 'concern:imperfections', label: 'Imperfections' },
      { value: 'concern:teint', label: 'Éclat' },
      { value: 'concern:protection', label: 'Protection' },
      { value: 'concern:sensibilite', label: 'Sensibilité' },
    ],
  },
  {
    code: 'texture',
    name: 'Texture',
    description: 'Aligné sur les choix `texture` du flow (`texture:*`).',
    tags: [
      { value: 'texture:legere', label: 'Texture légère' },
      { value: 'texture:confort', label: 'Texture confort' },
      { value: 'texture:sans-parfum', label: 'Sans parfum' },
    ],
  },
  {
    code: 'storefront',
    name: 'Vitrine',
    description: 'Accueil boutique (`/`) et PLP `/selection`.',
    tags: [{ value: 'storefront:featured', label: 'Mis en avant storefront' }],
  },
];
